import React, { useEffect, useState } from 'react';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { Stack, Button, Row, Col, Image, Form } from 'react-bootstrap';
import { useChangeMeetingStatusMutation, useGetCompanyMeetingListQuery } from 'services';
import ScheduleMeetingModal from 'components/Modals/Common/ScheduleMeeting';
import useCommonStore, { setModalConfig } from 'store/common';
import PaginationBox from 'components/PaginationBox';
import { handleAsyncOperation, isTimeGone } from 'utils';
import CopyButton from 'components/Common/CopyButton';
import useTranslate from 'hooks/useTranslate';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { dataLimits, S3_URL } from 'constants/index';
import './styles.scss';

const ScheduledMeetingsCompany: React.FC = () => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const [currentPage, setCurrentPage] = useState(1);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const [startSearch, setStartSearch] = useState<boolean>(false);

	const [payload, setPayload] = useState({
		limit: dataLimits.limits,
		order: 'ASC',
		keyword: '',
	});

	const { mutateAsync: changeStatusMutation } = useChangeMeetingStatusMutation();
	const { data: { docs = [], meta: { totalDocs = 10, limit = 1 } = {} } = [], refetch } =
		useGetCompanyMeetingListQuery(payload, currentPage);
	const [showModal, setShowModal] = useState(false);

	const handleModalClose = () => {
		setShowModal(!showModal);
	};

	useEffect(() => {
		refetch();
	}, [currentPage, startSearch]);

	const handleSearch = (e: any) => {
		const value = e.target.value?.trim();
		if (e.keyCode === 13 && value) {
			setStartSearch((prev: any) => !prev);
			setPayload((prev: any) => ({
				...prev,
				keyword: value,
			}));
		}
	};

	const handleOnChange = (e: any) => {
		if (e?.target?.value.length === 0) {
			setStartSearch((prev) => !prev);
			setPayload((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		} else {
			setPayload((prev: any) => ({
				...prev,
				keyword: e?.target.value,
			}));
		}
	};

	const handleCancelMeeting = (id: any) => {
		handleAsyncOperation(async () => {
			await changeStatusMutation({ meetingId: id, status: 'cancelled' });
			setCurrentPage(1);
			toast.success(translate('ToastMessage.meetingCancelled'));
			refetch();
		}, hideCommonModal);
	};

	const markAsDoneFunction = (id: any) => {
		handleAsyncOperation(async () => {
			await changeStatusMutation({ meetingId: id, status: 'completed' });
			refetch();
		}, hideCommonModal);
	};

	const handleChangeStatusPopup = (meetingId: any, check: boolean) => {
		setModalConfig({
			visible: true,
			id: meetingId,
			type: 'confirmModal',
			onClick: check ? () => handleCancelMeeting(meetingId) : () => markAsDoneFunction(meetingId),
			data: {
				title: check ? translate('Modal.wantToCancelMeeting') : translate('Modal.wantToMarkAsDone'),
			},
			onClose: null,
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const renderMeetingButton = (res: any) => {
		const isMeetingToday =
			moment().format('MM/DD/YYYY') === moment(res?.interviewDate).format('MM/DD/YYYY');
		const meetingIsInPast = moment(res?.interviewDate).isBefore(moment(), 'day');
		const isCompleted = res?.status === 'completed';
		const isTimePassed = isTimeGone(res?.interviewTime);
		// Check if the Cancel Meeting button is visible
		const isCancelButtonVisible =
			!isCompleted && ((!meetingIsInPast && !isMeetingToday) || (isMeetingToday && !isTimePassed));

		return (
			<>
				{/* Show meeting link and CopyButton only if Cancel button is visible */}
				<div
					className="d-flex align-items-center gap-2"
					style={{
						visibility: isCancelButtonVisible ? 'visible' : 'hidden',
					}}
				>
					<div className="meeting-link rounded-pill flex-1">
						<h6 className="mb-0">{res?.interviewLink}</h6>
					</div>
					<CopyButton text={res?.interviewLink} />
				</div>

				{isCancelButtonVisible && (
					<div className="d-flex align-items-center gap-2">
						<Button
							onClick={() => handleChangeStatusPopup(res?.id, true)}
							className="gray-transparent-btn rounded-pill lh-1 mt-auto"
						>
							{translate('Common.cancelMeeting')}
						</Button>
					</div>
				)}

				{/* Show no data if the time is passed and it's the same day */}
				{(meetingIsInPast || (isMeetingToday && isTimePassed)) && (
					<div className="meeting-link rounded-pill flex-1 text-center d-flex justify-content-center align-items-center">
						<h6 className="mb-0 fw-bold">{translate('Common.markAsDoneByCompany')}</h6>
					</div>
				)}
			</>
		);
	};

	const goToUserProfile = (userId: any) => {
		navigate(`/seeker/profile/${userId}`);
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold">{translate('Common.scheduledMeetings')}</h4>
						{docs?.length > 0 && (
							<p className="mb-0">
								{translate('Common.showingResults', { noOfResults: docs?.length.toString() })}
							</p>
						)}
					</div>
					<Form.Group className="user-search position-relative w-100">
						<i className="ri-search-line position-absolute"></i>
						<Form.Control
							className="rounded-pill"
							type="search"
							placeholder={translate('PlaceHolder.searchByUserName')}
							onKeyDown={handleSearch}
							onChange={handleOnChange}
							value={payload.keyword ?? ''}
						/>
					</Form.Group>
					{showModal && <ScheduleMeetingModal showModal={showModal} onClose={handleModalClose} />}
				</div>

				<Row className="scheduled-meetings">
					{docs?.length ? (
						docs?.map((res: any, index: any) => (
							<Col key={`${index}-${res?.jobId}-${res?.companyId}-${res?.seekerId}`} xxl={4} lg={4}>
								<Stack className="scheduled-meetings-box gap-3">
									<div className="d-flex gap-2 gap-md-3">
										<div className="scheduled-meetings-box-logo">
											<Image
												src={`${S3_URL}${res?.profile}`}
												onError={({ currentTarget }) => {
													currentTarget.onerror = null;
													currentTarget.src = IMAGE_PATHS.UserDefault;
												}}
												style={{ cursor: 'pointer' }}
												onClick={() => goToUserProfile(res?.seekerId)}
												alt="logo"
												className="w-100 h-100 object-fit-cover"
												loading="lazy"
											/>
										</div>
										<div>
											<h5
												style={{ cursor: 'pointer' }}
												onClick={() => goToUserProfile(res?.seekerId)}
												className="fw-bold"
											>
												{res?.userName}
											</h5>
											<h6 className="mb-0">{res?.title}</h6>
										</div>
									</div>
									<div className="date-time d-flex flex-wrap align-items-center gap-3">
										<div className="d-flex align-items-center gap-2">
											<i className="ri-calendar-line"></i>
											<p className="mb-0">{moment(res?.interviewDate).format('D MMM, YYYY')}</p>
										</div>
										<div className="d-flex align-items-center gap-2">
											<i className="ri-time-line"></i>
											<p className="mb-0">{res?.interviewTime}</p>
										</div>
									</div>

									{renderMeetingButton(res)}
								</Stack>
							</Col>
						))
					) : (
						<Stack>{translate('Common.noDataFound')}</Stack>
					)}
				</Row>
				{totalDocs > limit && (
					<PaginationBox
						totalData={totalDocs}
						limit={limit}
						page={currentPage}
						setCurrentPage={setCurrentPage}
					/>
				)}
			</Stack>
		</div>
	);
};

export default ScheduledMeetingsCompany;
